import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import { FaArrowLeft, FaUser, FaGraduationCap, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

function AdminUserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { reloadSubscription, user: authUser } = useAuth();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const hasReloadedSubscription = useRef(false);

    useEffect(() => {
        // Strict admin check using phone number
        const isAdmin = authUser && (authUser.phoneNumber === "9370329233" || authUser.phoneNumber === "+919370329233");
        const storedRole = localStorage.getItem("matrimonyUserRole");

        if (!isAdmin && storedRole !== "admin") {
            if (authUser) {
                // If logged in as regular user, redirect to standard user detail view
                navigate(`/user/${id}`);
            } else {
                // Not logged in at all, redirect to login
                navigate("/login");
            }
        }
    }, [authUser, navigate, id]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const db = getDatabase();
                const userRef = ref(db, `Matrimony/users/${id}`);
                const snapshot = await get(userRef);
                if (snapshot.exists()) {
                    setUser(snapshot.val());
                } else {
                    console.log("User not found");
                }
            } catch (error) {
                console.error("Error fetching user:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();

        if (reloadSubscription && !hasReloadedSubscription.current) {
            hasReloadedSubscription.current = true;
            reloadSubscription();
        }
    }, [id, reloadSubscription]);

    const calculateAge = (dob) => {
        if (!dob) return "Not specified";
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const isImageUrl = (url) => {
        if (!url || typeof url !== 'string') return false;

        // Check if it's a Firebase Storage URL (common for these images)
        if (url.includes('firebasestorage.googleapis.com')) return true;

        // Check standard image patterns/extensions
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        const lowerUrl = url.toLowerCase();

        return (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) &&
            (imageExtensions.some(ext => lowerUrl.includes(ext)) || url.includes('image'));
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Loading profile (Admin View)...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div style={styles.errorContainer}>
                <h2>User not found</h2>
                <button
                    onClick={() => navigate('/admin')}
                    style={styles.backButton}
                >
                    <FaArrowLeft /> Back to Admin Dashboard
                </button>
            </div>
        );
    }

    const personal = user.personal || {};
    const educational = user.educational || {};
    const contact = user.contact || {};
    const fullName = [personal.firstName, personal.middleName, personal.lastName].filter(Boolean).join(" ");

    const validPhotos = user.photos ? user.photos.filter(photo => photo && isImageUrl(photo)) : [];

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
    };

    const handleEdit = () => {
        navigate(`/registration?edit=${id}`);
    };

    const handleDelete = async () => {
        if (window.confirm(`Are you sure you want to delete profile of "${fullName || id}"? This action cannot be undone.`)) {
            try {
                setLoading(true);
                const { remove } = await import("firebase/database");
                const db = getDatabase();
                await remove(ref(db, `Matrimony/users/${id}`));
                alert('Profile deleted successfully');
                navigate('/admin');
            } catch (error) {
                console.error("Error deleting user:", error);
                alert("Failed to delete user. Please try again.");
                setLoading(false);
            }
        }
    };

    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <button
                    onClick={() => navigate('/admin')}
                    style={styles.backButton}
                >
                    <FaArrowLeft /> Back to Admin
                </button>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleEdit}
                        style={styles.editButton}
                    >
                        🛠️ Edit Profile
                    </button>
                    <button
                        onClick={handleDelete}
                        style={styles.deleteButton}
                    >
                        🗑️ Delete Profile
                    </button>
                    <div style={styles.adminBadge}>Admin View - Full Access</div>
                </div>
            </header>

            <div style={styles.profileHeader}>
                <div style={styles.profileImageContainer}>
                    {validPhotos.length > 0 ? (
                        <img
                            src={validPhotos[0]}
                            alt="User avatar"
                            style={styles.profileImage}
                            onClick={() => handleImageClick(validPhotos[0])}
                        />
                    ) : (
                        <div style={styles.placeholderImage}>
                            <FaUser style={{ fontSize: '4rem', color: '#64748b' }} />
                        </div>
                    )}
                </div>
                <div style={styles.profileInfo}>
                    <h1 style={styles.name}>{fullName || "Name not provided"}</h1>
                    <div style={styles.basicInfo}>
                        <div style={styles.infoItem}>
                            <FaCalendarAlt style={styles.icon} />
                            <span>{calculateAge(personal.dateOfBirth)} years old</span>
                        </div>
                        <div style={styles.infoItem}>
                            <FaMapMarkerAlt style={styles.icon} />
                            <span>{educational.currentPlace || educational.district || "Location not specified"}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <FaUser style={styles.icon} />
                            <span>{personal.gender || "Not specified"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div style={styles.sectionsContainer}>
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <FaUser style={styles.sectionIcon} />
                        Personal Information
                    </h2>
                    <div style={styles.detailsGrid}>
                        <div style={styles.detailItem}>
                            <strong>Full Name:</strong>
                            <span>{fullName || "Not provided"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Date of Birth:</strong>
                            <span>{personal.dateOfBirth || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Birth Time:</strong>
                            <span>{personal.birthTime || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Age:</strong>
                            <span>{calculateAge(personal.dateOfBirth)} years</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Height:</strong>
                            <span>
                                {personal.heightFeet && personal.heightInches
                                    ? `${personal.heightFeet}' ${personal.heightInches}"`
                                    : "Not specified"}
                            </span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Gender:</strong>
                            <span>{personal.gender || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Marital Status:</strong>
                            <span>{personal.maritalStatus || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Religion:</strong>
                            <span>{personal.religion || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Caste:</strong>
                            <span>{personal.caste || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Inter-Caste Marriage:</strong>
                            <span>{personal.interCasteAllowed || "Not specified"}</span>
                        </div>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <FaGraduationCap style={styles.sectionIcon} />
                        Education & Career
                    </h2>
                    <div style={styles.detailsGrid}>
                        <div style={styles.detailItem}>
                            <strong>Education:</strong>
                            <span>{educational.education || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Profession:</strong>
                            <span>{educational.profession || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Current Place:</strong>
                            <span>{educational.currentPlace || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Native Place:</strong>
                            <span>{educational.nativePlace || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Taluka:</strong>
                            <span>{educational.taluka || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>State:</strong>
                            <span>{educational.state || "Not specified"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>District:</strong>
                            <span>{educational.district || "Not specified"}</span>
                        </div>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <FaPhone style={styles.sectionIcon} />
                        Contact Information
                    </h2>
                    <div style={styles.detailsGrid}>
                        <div style={styles.detailItem}>
                            <strong>Phone Number:</strong>
                            <span>{personal.phoneNumber || "Not provided"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>WhatsApp Number:</strong>
                            <span>{contact.whatsappNumber || "Not provided"}</span>
                        </div>
                        <div style={styles.detailItem}>
                            <strong>Calling Number:</strong>
                            <span>{contact.callingNumber || "Not provided"}</span>
                        </div>
                    </div>
                </div>

                {validPhotos.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            📸 Photo Gallery
                        </h2>
                        <div style={styles.photoGallery}>
                            {validPhotos.map((photo, index) => (
                                <div key={index} style={styles.photoContainer}>
                                    <img
                                        src={photo}
                                        alt={`User ${index + 1}`}
                                        style={styles.photo}
                                        onClick={() => handleImageClick(photo)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {user.biodata && isImageUrl(user.biodata) && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            📄 Biodata
                        </h2>
                        <div style={styles.biodataContainer}>
                            <img
                                src={user.biodata}
                                alt="User document"
                                style={styles.biodataImage}
                                onClick={() => handleImageClick(user.biodata)}
                            />
                        </div>
                    </div>
                )}
            </div>

            {showImageModal && selectedImage && (
                <div style={styles.imageModalOverlay} onClick={closeImageModal}>
                    <div style={styles.imageModalContainer} onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={closeImageModal}
                            style={styles.imageModalCloseButton}
                        >
                            <FaTimes style={{ fontSize: '1.5rem' }} />
                        </button>
                        <img
                            src={selectedImage}
                            alt="Enlarged view"
                            style={styles.imageModalImage}
                        />
                        <div style={styles.imageModalControls}>
                            <button
                                onClick={() => window.open(selectedImage, '_blank')}
                                style={styles.imageModalButton}
                            >
                                Open in New Tab
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        fontFamily: 'Arial, sans-serif',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #6a11cb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '1rem'
    },
    loadingText: {
        fontSize: '1.2rem',
        color: '#64748b',
        fontWeight: '600'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        textAlign: 'center'
    },
    header: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
    },
    adminBadge: {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '20px',
        fontWeight: 'bold',
        fontSize: '0.9rem'
    },
    editButton: {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        padding: '0.5rem 1.2rem',
        borderRadius: '25px',
        fontWeight: 'bold',
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem'
    },
    deleteButton: {
        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        padding: '0.5rem 1.2rem',
        borderRadius: '25px',
        fontWeight: 'bold',
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem'
    },
    backButton: {
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        color: 'white',
        border: 'none',
        padding: '0.75rem 1.5rem',
        borderRadius: '25px',
        cursor: 'pointer',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease'
    },
    profileHeader: {
        background: 'white',
        margin: '2rem',
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '2rem',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    profileImageContainer: {
        position: 'relative'
    },
    profileImage: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: 'center center',
        border: '4px solid #6a11cb',
        boxShadow: '0 8px 25px rgba(106, 17, 203, 0.3)',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    placeholderImage: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '4px solid #6a11cb'
    },
    profileInfo: {
        flex: 1,
        minWidth: '300px'
    },
    name: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        margin: '0 0 1rem 0',
        color: '#1e293b',
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    },
    basicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem'
    },
    infoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '1.1rem',
        color: '#64748b'
    },
    icon: {
        color: '#6a11cb',
        fontSize: '1.2rem'
    },
    sectionsContainer: {
        padding: '0 2rem 2rem'
    },
    section: {
        background: 'white',
        margin: '2rem 0',
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#1e293b',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #e2e8f0'
    },
    sectionIcon: {
        color: '#6a11cb',
        fontSize: '1.5rem'
    },
    detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem'
    },
    detailItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '10px',
        border: '1px solid #e2e8f0'
    },
    photoGallery: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '1.5rem'
    },
    photoContainer: {
        position: 'relative',
        borderRadius: '15px',
        overflow: 'hidden',
        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease'
    },
    photo: {
        width: '100%',
        height: '350px',
        objectFit: 'cover',
        objectPosition: 'center top',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderRadius: '15px',
        backgroundColor: '#f8fafc'
    },
    biodataContainer: {
        display: 'flex',
        justifyContent: 'center'
    },
    biodataImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '15px',
        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    imageModalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem'
    },
    imageModalContainer: {
        position: 'relative',
        maxWidth: '90vw',
        maxHeight: '90vh',
        background: 'white',
        borderRadius: '15px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    imageModalCloseButton: {
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        background: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2001
    },
    imageModalImage: {
        width: '100%',
        height: 'auto',
        maxHeight: '80vh',
        objectFit: 'contain',
        display: 'block'
    },
    imageModalControls: {
        padding: '1rem',
        background: 'white',
        display: 'flex',
        justifyContent: 'center'
    },
    imageModalButton: {
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        color: 'white',
        border: 'none',
        padding: '0.75rem 1.5rem',
        borderRadius: '25px',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1rem',
        transition: 'all 0.3s ease'
    }
};

// Add CSS animations
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        
        .photo-hover:hover {
            transform: scale(1.05);
        }
        
        .biodata-hover:hover {
            transform: scale(1.02);
        }
    `;
    document.head.appendChild(styleSheet);
}

export default AdminUserDetail;
